import { useState } from 'react';
import type { InterviewQuestion } from '@wordpuppi/shared';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export interface InterviewAnswer {
  text: string;
  picks: string[];
}

export const answerText = (q: InterviewQuestion, a: InterviewAnswer | undefined) =>
  [...(a?.picks ?? q.defaults ?? []), a?.text.trim()].filter(Boolean).join(', ');

/**
 * The new-site interview: Baxter's first-turn questions, asked here one at a
 * time before the site exists, so the chat opens on a plan instead of a list.
 */
export function SiteInterview({
  questions,
  answers,
  onAnswer,
  onBack,
  onDone,
  busy,
}: {
  questions: InterviewQuestion[];
  answers: Record<string, InterviewAnswer>;
  onAnswer: (id: string, answer: InterviewAnswer) => void;
  /** Leave the interview for the form (from the first question). */
  onBack: () => void;
  onDone: () => void;
  busy: boolean;
}) {
  const [step, setStep] = useState(0);
  // Which way the current question arrived; unset on the first one, which rides the whole-interview entrance.
  const [dir, setDir] = useState<'forward' | 'back'>();
  const q = questions[step];
  const last = step === questions.length - 1;
  const answer = answers[q.id] ?? { text: '', picks: q.defaults ?? [] };

  const go = (to: number) => { setDir(to > step ? 'forward' : 'back'); setStep(to); };
  const next = () => (last ? onDone() : go(step + 1));
  const skip = () => { onAnswer(q.id, { text: '', picks: [] }); next(); };
  const back = () => (step === 0 ? onBack() : go(step - 1));
  const toggle = (pick: string) =>
    onAnswer(q.id, {
      ...answer,
      picks: answer.picks.includes(pick) ? answer.picks.filter((p) => p !== pick) : [...answer.picks, pick],
    });

  return (
    <div className="interview-enter space-y-5" data-testid="ai-interview">
      <div className="flex items-center gap-3">
        <div
          className="h-1 flex-1 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={questions.length}
          aria-valuenow={step + 1}
          aria-label={`Question ${step + 1} of ${questions.length}`}
        >
          <div
            className="h-full w-full rounded-full bg-primary transition-transform duration-(--duration-step) ease-out motion-reduce:transition-none"
            style={{ transform: `translateX(-${100 - ((step + 1) / questions.length) * 100}%)` }}
          />
        </div>
        <span className="text-xs tabular-nums text-muted-foreground">
          {step + 1} of {questions.length}
        </span>
      </div>

      <div key={q.id} className="interview-step space-y-3" data-dir={dir}>
        <h2 className="text-xl font-semibold" id="ai-interview-question">
          {q.question}
        </h2>
        {q.picks && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="ai-interview-question">
            {q.picks.map((pick) => (
              <Button
                key={pick}
                type="button"
                size="sm"
                variant={answer.picks.includes(pick) ? 'default' : 'outline'}
                aria-pressed={answer.picks.includes(pick)}
                onClick={() => toggle(pick)}
              >
                {pick}
              </Button>
            ))}
          </div>
        )}
        <Textarea
          autoFocus
          data-testid="ai-interview-answer"
          aria-labelledby="ai-interview-question"
          rows={3}
          value={answer.text}
          placeholder={q.placeholder}
          onChange={(e) => onAnswer(q.id, { ...answer, text: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              next();
            }
          }}
          className="resize-none"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" onClick={back} disabled={busy}>
          <ArrowLeft /> Back
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {!last && (
            <Button type="button" variant="ghost" onClick={skip} disabled={busy} data-testid="ai-interview-skip">
              Skip
            </Button>
          )}
          <Button type="button" onClick={next} disabled={busy} data-testid="ai-interview-next">
            {last ? (
              <>
                <Sparkles /> {busy ? 'Preparing your site…' : 'Build it'}
              </>
            ) : (
              'Next'
            )}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Press Enter for the next question. Skip any you'd rather leave to Baxter.</p>
    </div>
  );
}
